import type { Deck } from '../types'

export const aiSlopPreventionMain: Deck = {
  id: 'ai-slop-prevention-main',
  workshopSlug: 'ai-slop-prevention-2026',
  type: 'main',
  title: 'AI Slop Prevention Workshop',
  label: 'Main Deck',
  slides: [
    // ── INTRO + AGENDA ──────────────────────────────────────
    {
      layout: 'title',
      tagline: '#AIAgentWeek2026',
      headline: 'AI Slop Prevention',
      subline: 'Five practices to ship clean code with agentic tools',
      bottomTagline: 'NYC  ·  May 5, 2026',
    },
    {
      layout: 'sectionDivider',
      sectionNumber: '00',
      title: 'Intro + Agenda',
      timeEstimate: '~5 min',
      subtitle: 'Why we\'re here',
    },
    {
      layout: 'bullets',
      headline: 'Your Hosts',
      items: [
        'Geoffrey Sechter — [Lightstrike Labs](https://lightstrikelabs.com), AI-first product engineering',
        'Taraqur Rahman — [Coding Nonslop Nonstop](https://luma.com/coding-nonslop-nonstop-nyc) AI NYC',
      ],
      accent: 'orange',
    },
    {
      layout: 'bullets',
      headline: 'Housekeeping',
      items: [
        'Wi-Fi: Farmer Member / organicfarm',
        'Bathrooms are at the back',
      ],
      accent: 'dim',
    },
    {
      layout: 'cascade',
      headline: 'The Bitter Lesson',
      lines: [
        'Models get better every quarter.',
        'Tooling changes every month.',
        'The big players are investing billions.',
        'Don\'t overfit your harness to today\'s workflow.',
      ],
      punchline: 'Stay nimble. Focus on fundamentals.',
      accent: 'orange',
    },
    {
      layout: 'comparison',
      headline: 'Vibe Coding vs Agentic Engineering',
      before: {
        label: 'Vibe Code / Prototype',
        content: 'Fast iteration\nDiscover the shape of the problem\nDisposable by design\nMinimal planning overhead\nLow token cost',
      },
      after: {
        label: 'Agentic Engineering',
        content: 'Deliberate architecture\nAudit trail and traceability\nBuilt to live with\nCognitive planning investment\nHigher token + time cost',
      },
      verdict: 'Both are valid. Knowing when to use which is the skill.',
      accent: 'orange',
    },
    {
      layout: 'bullets',
      headline: 'Today\'s Agenda',
      items: [
        'Demo — see the spectrum in action',
        'Core Concepts — five practices that separate prototype from production',
        'Work Time — apply them to your own repo',
        'Check-in — share progress, surface blockers',
        'More Work Time — keep building',
        'Wrap-up — what you\'re taking home',
      ],
      accent: 'amber',
    },

    // ── DEMO ────────────────────────────────────────────────
    {
      layout: 'sectionDivider',
      sectionNumber: '01',
      title: 'Demo',
      timeEstimate: '~5 min',
      subtitle: 'The spectrum in practice',
    },
    {
      layout: 'split',
      headline: 'repo-analyzer-red',
      body: 'Vibe coded. Fast to build, great for discovering the shape of the problem. Not something you\'d ever want to live with.\n\n[github.com/lightstrikelabs/repo-analyzer-red](https://github.com/lightstrikelabs/repo-analyzer-red)',
      accent: 'orange',
    },
    {
      layout: 'split',
      headline: 'repo-analyzer-green',
      body: 'Agentically engineered. Better audit trail, more deliberate architecture. More work — cognitive planning, token usage, time — but built to ship.\n\n[github.com/lightstrikelabs/repo-analyzer-green](https://github.com/lightstrikelabs/repo-analyzer-green)',
      accent: 'amber',
    },

    // ── CORE CONCEPTS ───────────────────────────────────────
    {
      layout: 'sectionDivider',
      sectionNumber: '02',
      title: 'Core Concepts',
      timeEstimate: '~20 min',
      subtitle: 'Five practices that move you from red to green',
    },

    // Practice 1: Scope
    {
      layout: 'split',
      headline: '1. Scope Sessions & Pull Requests (PRs)',
      body: 'AI agents work best with a narrow, well-defined task. Sprawling sessions produce sprawling diffs. Sprawling diffs hide bugs, break reviews, and erode trust.',
      accent: 'orange',
    },
    {
      layout: 'codeExample',
      headline: 'SESSION.md Scoping Pattern',
      language: 'markdown',
      code: '# Task Scope\n\n## Current Session\n- Implement user avatar upload\n- Add file type validation (PNG, JPG, WebP)\n- Max size: 5MB\n\n## Out of Scope\n- Image cropping UI (next PR)\n- CDN migration (separate initiative)',
      annotations: [
        'Define what\'s IN scope explicitly',
        'Name what\'s OUT to prevent drift',
      ],
      accent: 'amber',
    },

    // Practice 2: Linting & Types
    {
      layout: 'split',
      headline: '2. Enforce Linting & Type Checks',
      body: 'A linter never gets tired, never skims, never rubber-stamps. Type checks catch structural errors before runtime. Together they create a floor that AI-generated code must clear before a human looks at it.',
      accent: 'orange',
    },
    {
      layout: 'codeExample',
      headline: 'Strict TypeScript Config',
      language: 'json',
      code: '{\n  "compilerOptions": {\n    "strict": true,\n    "noUncheckedIndexedAccess": true,\n    "noImplicitOverride": true,\n    "exactOptionalPropertyTypes": true\n  }\n}',
      annotations: [
        'strict: true enables all strict checks at once',
        'noUncheckedIndexedAccess catches array/object access bugs',
      ],
      accent: 'amber',
    },

    // Practice 3: E2E Tests
    {
      layout: 'split',
      headline: '3. Write E2E Integration Tests',
      body: 'Unit tests verify functions. E2E tests verify features. When AI rewrites your internals, unit tests break even when the feature works. E2E tests break only when the feature is actually broken.',
      accent: 'orange',
    },

    // Practice 4: PRs with CI
    {
      layout: 'split',
      headline: '4. PRs with Required Continuous Integration (CI)',
      body: 'A PR without CI is a suggestion. A PR with green CI is a contract. Required checks ensure every change — human or AI — passes the same quality bar before it reaches main.',
      accent: 'orange',
    },

    // Practice 5: Software Fundamentals
    {
      layout: 'cascade',
      headline: '5. Software Fundamentals',
      lines: [
        'Naming things well.',
        'Knowing when NOT to abstract.',
        'Separating concerns.',
        'Understanding your domain.',
        'Recognizing when code smells.',
      ],
      punchline: 'These are your job. They always were.',
      accent: 'orange',
    },

    // ── WORK TIME 1 ─────────────────────────────────────────
    {
      layout: 'sectionDivider',
      sectionNumber: '03',
      title: 'Work Time',
      timeEstimate: '~40 min',
      subtitle: 'Pick a practice, apply it to your repo',
    },
    {
      layout: 'exercise',
      headline: 'Exercise: Scope One Change',
      objective: 'Write a SESSION.md scope block for a real task in your repo',
      instructions: [
        'Pick one feature or fix you need to ship',
        'Write 3-5 bullet points for what\'s in scope',
        'Write 2-3 bullet points for what\'s explicitly out',
        'Commit it to a new branch',
      ],
      deliverable: 'A committed SESSION.md with clear scope boundaries',
    },
    {
      layout: 'exercise',
      headline: 'Exercise: Tighten One Rule',
      objective: 'Enable one new strict TypeScript or ESLint rule in your project',
      instructions: [
        'Open your tsconfig.json or .eslintrc',
        'Enable one rule you don\'t currently enforce',
        'Run the check — fix or suppress any new errors',
        'Commit the config change and fixes',
      ],
      deliverable: 'A stricter config with zero violations',
    },
    {
      layout: 'exercise',
      headline: 'Exercise: Write One E2E Test',
      objective: 'Add a Playwright or Cypress test for your app\'s most critical flow',
      instructions: [
        'Identify your app\'s #1 user journey',
        'Write a test that exercises the happy path end-to-end',
        'Run it locally — make it pass',
        'Add it to your CI pipeline',
      ],
      deliverable: 'A passing E2E test committed to your repo',
    },
    {
      layout: 'exercise',
      headline: 'Exercise: Set Up CI',
      objective: 'Add a GitHub Actions workflow with required status checks',
      instructions: [
        'Create .github/workflows/ci.yml',
        'Add steps: lint → typecheck → test → build',
        'Push and open a PR to verify it runs',
        'Enable required status checks in repo settings',
      ],
      deliverable: 'A green CI pipeline on an open PR',
    },
    {
      layout: 'exercise',
      headline: 'Exercise: Code Review',
      objective: 'Review a PR from your neighbor with fresh eyes',
      instructions: [
        'Swap repos with someone at your table',
        'Read the diff — does the scope hold?',
        'Check naming, boundaries, error handling',
        'Leave one actionable comment',
      ],
      deliverable: 'A constructive code review comment',
    },

    // ── CHECK-IN ────────────────────────────────────────────
    {
      layout: 'sectionDivider',
      sectionNumber: '04',
      title: 'Check-in',
      timeEstimate: '~10 min',
      subtitle: 'How\'s it going?',
    },
    {
      layout: 'title',
      headline: 'Show & Tell',
      subline: 'What did you ship? What blocked you?',
      accent: 'amber',
    },

    // ── WORK TIME 2 ─────────────────────────────────────────
    {
      layout: 'sectionDivider',
      sectionNumber: '05',
      title: 'Work Time',
      timeEstimate: '~20 min',
      subtitle: 'Keep building — try another practice',
    },
    {
      layout: 'title',
      headline: 'Back to Work',
      subline: 'Pick up where you left off, or tackle a new practice',
      accent: 'dim',
    },

    // ── WRAP-UP ─────────────────────────────────────────────
    {
      layout: 'sectionDivider',
      sectionNumber: '06',
      title: 'Wrap-Up',
      timeEstimate: '~10 min',
      subtitle: 'What you\'re taking home',
    },
    {
      layout: 'checklist',
      headline: 'Workshop Deliverables',
      items: [
        { text: 'One pattern enforced in your repository', checked: false },
        { text: 'An open PR with green CI', checked: false },
        { text: 'A configuration cheat sheet', checked: false },
        { text: 'Vocabulary to evaluate code quality critically', checked: false },
      ],
      instruction: 'How many did you check off?',
      accent: 'orange',
    },
    {
      layout: 'steps',
      headline: 'What Happens Next',
      steps: [
        { text: 'Ship your PR from today', done: false },
        { text: 'Add one more practice this week', done: false },
        { text: 'Share what you learned with your team', done: false },
        { text: 'Join the [Nonslop Nonstop](https://discord.gg/7bX9ekP7) community', done: false },
      ],
      prompt: 'Start with one. Build from there.',
      accent: 'orange',
    },
    {
      layout: 'cta',
      headline: 'Stay Connected',
      body: '[Lightstrike Labs](https://lightstrikelabs.com) — AI-first product engineering consultancy specializing in code remediation after agent implementation.',
      linkText: 'lightstrikelabs.com',
      href: 'https://lightstrikelabs.com',
      links: [
        { text: 'Nonslop Nonstop Discord', href: 'https://discord.gg/7bX9ekP7' },
        { text: 'Nonslop Nonstop Luma', href: 'https://luma.com/coding-nonslop-nonstop-nyc' },
      ],
      accent: 'orange',
    },

    // ── ADDENDUM: CONFIGURATION CHEAT SHEET (page 1) ────────
    {
      layout: 'reference',
      headline: 'Configuration Cheat Sheet',
      sections: [
        {
          heading: 'SESSION.md Template',
          items: [
            '## Current Session: 3-5 in-scope bullets',
            '## Out of Scope: name what you won\'t do',
            '## Constraints: tech, time, or policy limits',
            'Commit to branch before starting agent session',
          ],
        },
        {
          heading: '[TypeScript](https://www.typescriptlang.org/tsconfig) Strict Mode',
          items: [
            '[strict](https://www.typescriptlang.org/tsconfig/#strict): true: all strict checks at once',
            '[noUncheckedIndexedAccess](https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess): true: safe array/object access',
            '[noImplicitOverride](https://www.typescriptlang.org/tsconfig/#noImplicitOverride): true: explicit override keyword',
            '[exactOptionalPropertyTypes](https://www.typescriptlang.org/tsconfig/#exactOptionalPropertyTypes): true: undefined vs missing',
          ],
        },
        {
          heading: '[ESLint](https://eslint.org/) Essentials',
          items: [
            '[@typescript-eslint/no-explicit-any](https://typescript-eslint.io/rules/no-explicit-any/): ban any type',
            '[@typescript-eslint/no-floating-promises](https://typescript-eslint.io/rules/no-floating-promises/): enforce await',
            '[no-console](https://eslint.org/docs/latest/rules/no-console): warn on console.log in production code',
            '[import/no-cycle](https://github.com/import-js/eslint-plugin-import/blob/main/docs/rules/no-cycle.md): prevent circular dependencies',
          ],
        },
        {
          heading: 'CI Pipeline ([GitHub Actions](https://docs.github.com/en/actions))',
          items: [
            'on: [pull_request]: run on every PR',
            'npm run lint: zero warnings allowed',
            'npm run typecheck: strict compilation',
            'npm run test: unit + E2E tests pass',
            'npm run build: production build succeeds',
            '[Required status checks](https://docs.github.com/en/repositories/configuring-branches-and-merges-in-your-repository/managing-rulesets) in repo settings',
          ],
        },
        {
          heading: 'PR Checklist',
          items: [
            '≤10 files changed per PR',
            'Clear title with [conventional prefix](https://www.conventionalcommits.org/) (feat/fix/chore)',
            'Description explains WHY, not just WHAT',
            'Single responsibility: one concern per PR',
            'Self-reviewed diff before requesting review',
          ],
        },
        {
          heading: 'E2E Test Scaffold ([Playwright](https://playwright.dev/))',
          items: [
            '[npm init playwright@latest](https://playwright.dev/docs/intro): project setup',
            'test.describe → test → page.goto → assertions',
            'Use [data-testid](https://playwright.dev/docs/locators#locate-by-test-id) for resilient selectors',
            'Assert on user-visible outcomes, not internals',
            'npx playwright test --ui: debug visually',
          ],
        },
      ],
      footer: 'AI Slop Prevention Workshop: [lightstrikelabs.com](https://lightstrikelabs.com)',
      accent: 'orange',
    },

    // ── ADDENDUM: CONFIGURATION CHEAT SHEET (page 2) ────────
    {
      layout: 'reference',
      headline: 'Issue Tracking Setup Guide',
      sections: [
        {
          heading: '[GitHub Issues](https://docs.github.com/en/issues) Baseline',
          items: [
            'Enable Issues in repo settings: single source of truth for work',
            'Every PR links to an issue: no orphan changes',
            'Close issues via commit messages: "fixes #42"',
            'Use issue templates for bugs, features, and tasks',
          ],
        },
        {
          heading: '[Labels](https://docs.github.com/en/issues/using-labels-and-milestones-to-track-work/managing-labels)',
          items: [
            'bug / feature / chore / docs: type labels',
            'priority: critical / high / medium / low',
            'status: needs-triage / in-progress / blocked',
            'area: frontend / backend / infra / ci: scope labels',
          ],
        },
        {
          heading: '[Milestones](https://docs.github.com/en/issues/using-labels-and-milestones-to-track-work/about-milestones)',
          items: [
            'Group issues by release or sprint deadline',
            'Set due dates for time-bound goals',
            'Track completion percentage per milestone',
            'Close milestones when all issues resolve',
          ],
        },
        {
          heading: '[GitHub Projects](https://docs.github.com/en/issues/planning-and-tracking-with-projects)',
          items: [
            'Board view: Backlog → In Progress → Review → Done',
            'Custom fields: effort estimate, sprint, team',
            'Automations: auto-move on PR merge or issue close',
            'Filter views per assignee, label, or milestone',
          ],
        },
        {
          heading: 'Adapting to Other Tools',
          items: [
            '[Linear](https://linear.app/docs): cycles, triage, auto-close from Git',
            '[Jira](https://www.atlassian.com/software/jira): sprints, epics, smart commits',
            '[Shortcut](https://shortcut.com/): stories, iterations, Git integration',
            'Core principle: every code change traces to a tracked unit of work',
          ],
        },
        {
          heading: 'Agent Integration',
          items: [
            'Reference issue numbers in SESSION.md scope',
            'Agent commits should include issue references',
            'Use labels to flag agent-generated work for review',
            'Track agent vs human contributions per milestone',
          ],
        },
      ],
      footer: 'AI Slop Prevention Workshop: [lightstrikelabs.com](https://lightstrikelabs.com)',
      accent: 'orange',
    },

    // ── ADDENDUM: CODE QUALITY VOCABULARY (page 1) ──────────
    {
      layout: 'reference',
      headline: 'Code Quality Vocabulary',
      sections: [
        {
          heading: 'Architecture Boundaries',
          items: [
            'Module cohesion: does each module have a [single purpose](https://en.wikipedia.org/wiki/Single-responsibility_principle)?',
            'Dependency direction: do [imports flow one way](https://en.wikipedia.org/wiki/Dependency_inversion_principle)?',
            'Separation of concerns: is business logic decoupled from I/O?',
            'Layer violations: are domain types leaking into UI?',
          ],
        },
        {
          heading: 'Maintainability',
          items: [
            'Deferred work markers: TODO, FIXME, HACK, XXX count',
            'Large file detection: files over threshold signal need to split',
            'Naming consistency: do identifiers follow project conventions?',
            'Premature abstraction: three similar lines > one clever helper',
          ],
        },
        {
          heading: 'Verifiability',
          items: [
            'Test-to-source ratio: are critical paths covered?',
            'E2E vs unit coverage: features tested, not just functions',
            'CI pipeline presence: are [checks required](https://docs.github.com/en/repositories/configuring-branches-and-merges-in-your-repository/managing-rulesets) before merge?',
            'Build reproducibility: does npm ci + build succeed clean?',
          ],
        },
        {
          heading: 'Security Hygiene',
          items: [
            '[Lockfile](https://docs.npmjs.com/cli/v10/configuring-npm/package-lock-json) present: pinned dependency versions',
            'No secrets in source: scan for API keys, tokens, credentials',
            '.env.example exists: document required env vars',
            'Dependency count: large surface area = larger attack surface',
          ],
        },
        {
          heading: 'Operability',
          items: [
            'Deployment pipeline: can you ship with one command?',
            'Rollback strategy: can you undo a bad deploy?',
            'Health checks: does the system report its own status?',
            'Structured logging: can you debug from logs alone?',
          ],
        },
        {
          heading: 'Rating Scale',
          items: [
            'Excellent: exceeds industry norms, actively maintained',
            'Good: solid practices, minor gaps only',
            'Fair: functional but missing key safeguards',
            'Weak: significant gaps creating real risk',
            'Poor: critical issues requiring immediate attention',
          ],
        },
      ],
      footer: 'Vocabulary derived from [repo-analyzer-green](https://github.com/lightstrikelabs/repo-analyzer-green): [Lightstrike Labs](https://lightstrikelabs.com)',
      accent: 'amber',
    },

    // ── ADDENDUM: CODE QUALITY VOCABULARY (page 2) ──────────
    {
      layout: 'reference',
      headline: 'Learning References for Reviewers',
      sections: [
        {
          heading: 'Code Review Foundations',
          items: [
            '[Google Engineering Practices: Code Review](https://google.github.io/eng-practices/review/): the industry standard guide',
            '[Conventional Comments](https://conventionalcomments.org/): structured feedback labels (suggestion, issue, praise)',
            '[Ship / Show / Ask](https://martinfowler.com/articles/ship-show-ask.html): when to merge, demo, or block',
            '[The Art of Giving and Receiving Code Reviews](https://www.alexandra-hill.com/2018/06/25/the-art-of-giving-and-receiving-code-reviews/): gracefully',
          ],
        },
        {
          heading: 'Architecture & Design',
          items: [
            '[Clean Architecture](https://blog.cleancoder.com/uncle-bob/2012/08/13/the-clean-architecture.html): dependency rules and boundary layers',
            '[SOLID Principles](https://en.wikipedia.org/wiki/SOLID): five foundational design principles',
            '[Twelve-Factor App](https://12factor.net/): methodology for cloud-native services',
            '[Patterns of Enterprise Application Architecture](https://martinfowler.com/eaaCatalog/): catalog by Martin Fowler',
          ],
        },
        {
          heading: 'Testing Strategy',
          items: [
            '[Testing Trophy](https://kentcdodds.com/blog/the-testing-trophy-and-testing-classifications): Kent C. Dodds on integration-first testing',
            '[Playwright Best Practices](https://playwright.dev/docs/best-practices): resilient E2E test patterns',
            '[Test Desiderata](https://kentbeck.github.io/TestDesiderata/): Kent Beck\'s 12 properties of good tests',
            '[Testing Without Mocks](https://www.jamesshore.com/v2/projects/testing-without-mocks/testing-without-mocks): nullables pattern by James Shore',
          ],
        },
        {
          heading: 'Agentic Code Review',
          items: [
            '[repo-analyzer-green](https://github.com/lightstrikelabs/repo-analyzer-green): structured repo quality audit tool',
            '[Anthropic Claude Code Best Practices](https://docs.anthropic.com/en/docs/build-with-claude/prompt-engineering/claude-code-best-practices): effective agent prompting',
            '[CodeRabbit](https://docs.coderabbit.ai/): AI-powered PR review automation',
            '[Greptile](https://docs.greptile.com/): codebase-aware AI review',
          ],
        },
        {
          heading: 'Security & Supply Chain',
          items: [
            '[OWASP Top 10](https://owasp.org/www-project-top-ten/): most critical web application security risks',
            '[Socket.dev](https://socket.dev/): detect supply chain attacks in dependencies',
            '[npm audit](https://docs.npmjs.com/cli/v10/commands/npm-audit): built-in vulnerability scanning',
            '[Snyk](https://snyk.io/product/open-source-security-management/): continuous dependency monitoring',
          ],
        },
        {
          heading: 'Further Reading',
          items: [
            '[The Bitter Lesson](http://www.incompleteideas.net/IncsightIdea/BitterLesson.html): Rich Sutton on scaling vs engineering',
            '[A Philosophy of Software Design](https://web.stanford.edu/~ouster/cgi-bin/book.php): John Ousterhout on complexity',
            '[Refactoring](https://refactoring.com/): Martin Fowler\'s catalog of code transformations',
            '[The Pragmatic Programmer](https://pragprog.com/titles/tpp20/): timeless software craftsmanship',
          ],
        },
      ],
      footer: 'Curated for human and agentic reviewers: [Lightstrike Labs](https://lightstrikelabs.com)',
      accent: 'amber',
    },
  ],
}
